import type {
  HelperMutationRecord,
  HelperMutationType,
} from '../helper/helperOutboxDb'
import type {
  HelperOperationPhase,
  HelperSyncState,
} from '../helper/helperStore'

export type MobileSyncRole = 'HELPER' | 'MEMBER'
export type MobileSyncTone = 'healthy' | 'working' | 'offline' | 'attention'
export type MobileOperationState = 'QUEUED' | 'UPLOADING' | 'FINALIZING' | 'SYNCING' | 'FAILED' | 'SYNCED'
export type MobileFailureKind = 'CONFLICT' | 'HIDDEN' | 'MISSING_LOCAL_FILE' | 'UPLOAD' | 'GENERAL'
export type PendingKindCount = { type: HelperMutationType; label: string; count: number }

const TRACKED_MUTATION_TYPES: HelperMutationType[] = [
  'CHECKLIST_PROOF_UPLOAD',
  'CHECKLIST_TOGGLE',
  'CHECKLIST_COMMENT',
  'CHECKLIST_VOICE_MEMO',
]

export const bucketPendingByKind = (
  records: ReadonlyArray<Pick<HelperMutationRecord, 'type'>>,
  labels: Record<HelperMutationType, string>,
): PendingKindCount[] => TRACKED_MUTATION_TYPES.map((type) => ({
  type,
  label: labels[type],
  count: records.reduce((sum, record) => sum + (record.type === type ? 1 : 0), 0),
}))

export type MobileSyncSnapshotInput = {
  syncState: HelperSyncState
  pendingCount: number
  failedCount: number
  completedCount: number
  lastSyncAt: number | null
  isOnline: boolean
}

const TYPE_LABEL: Record<HelperMutationType, string> & Record<string, string> = {
  CHECKLIST_TOGGLE: 'Status update',
  CHECKLIST_PROOF_UPLOAD: 'Proof upload',
  CHECKLIST_COMMENT: 'Comment',
  CHECKLIST_VOICE_MEMO: 'Voice memo',
  WORKSPACE_ACTIVITY: 'Workspace activity',
  STOCK_COUNT_ROUND: 'Stock count round',
  STOCK_SCAN_SUGGESTION: 'Stock scan suggestion',
  PENDING_EXPENSE_INTAKE: 'Expense intake',
  EDU_BOARDING: 'Bus boarding',
  EDU_ATTENDANCE_MARK: 'Class attendance',
  TIME_CLOCK: 'Time clock',
  OPS_DUE_RECORD: 'Due record',
  OPS_CHECKPOINT_SCAN: 'Checkpoint scan',
  OPS_SKIP_DUE: 'Skipped due',
  OPS_RAISE_REQUEST: 'Request',
  OPS_ATTENDANCE_ROLL: 'Attendance roll',
  OPS_START_REQUEST: 'Start request',
  OPS_MARK_REQUEST_DONE: 'Mark request done',
  OPS_BMW_SAVE: 'BMW daily draft',
  OPS_DPR_SAVE: 'Daily progress report',
  OPS_CON_ISSUE_CREATE: 'Hindrance report',
  OPS_CON_ISSUE_ATTACH_PHOTOS: 'Hindrance photos',
  CON_ISSUE_ATTACH_PHOTOS: 'Hindrance photos',
  BREAKDOWN: 'Breakdown',
  QUALITY_HOLD: 'Quality hold',
  NEAR_MISS: 'Near miss',
}

export const getMobileOperationTitle = (record: Pick<HelperMutationRecord, 'type' | 'payload'>): string => {
  const data = record.payload as Record<string, unknown>
  const target = typeof data.fileName === 'string' && data.fileName.trim()
    ? data.fileName.trim()
    : typeof data.title === 'string' && data.title.trim()
      ? data.title.trim().slice(0, 42)
      : typeof data.issueId === 'string' && data.issueId.trim()
        ? `Issue ${data.issueId.trim().slice(0, 8)}`
        : typeof data.body === 'string' && data.body.trim()
          ? data.body.trim().slice(0, 42)
        : typeof data.checklistItemId === 'string'
          ? `Task ${data.checklistItemId.slice(0, 8)}`
          : 'Workspace task'
  const isBreakdown =
    record.type === 'OPS_RAISE_REQUEST' &&
    (data.kindKey === 'BREAKDOWN' || data.kind === 'BREAKDOWN')
  const isQualityHold =
    record.type === 'OPS_RAISE_REQUEST' &&
    (data.kindKey === 'QUALITY_HOLD' || data.kind === 'QUALITY_HOLD')
  const isNearMiss =
    record.type === 'OPS_RAISE_REQUEST' &&
    (data.kindKey === 'NEAR_MISS' || data.kind === 'NEAR_MISS')
  const typeLabel = isBreakdown
    ? 'Breakdown'
    : isQualityHold
      ? 'Quality hold'
      : isNearMiss
        ? 'Near miss'
        : TYPE_LABEL[record.type] || 'Sync item'
  return `${typeLabel} · ${target}`
}

export const getMobileOperationTargetId = (record: Pick<HelperMutationRecord, 'payload'>): string | null => {
  const data = record.payload as Record<string, unknown>
  return typeof data.checklistItemId === 'string' ? data.checklistItemId : null
}

export const getMobileOperationWorkspaceId = (record: Pick<HelperMutationRecord, 'payload'>): string | null => {
  const data = record.payload as Record<string, unknown>
  return typeof data.workspaceId === 'string' ? data.workspaceId : null
}

export const getMobileFailureKind = (error: string | null | undefined): MobileFailureKind => {
  if (!error) return 'GENERAL'
  if (error === 'CONFLICT_NEWER_SERVER') return 'CONFLICT'
  if (error === 'CHECKLIST_HIDDEN_FROM_HELPER') return 'HIDDEN'
  if (/local .*blob missing/i.test(error)) return 'MISSING_LOCAL_FILE'
  if (/upload|reservation|quota|storage/i.test(error)) return 'UPLOAD'
  return 'GENERAL'
}

export const getMobileFailureMessage = (error: string | null | undefined): string => {
  const kind = getMobileFailureKind(error)
  if (kind === 'CONFLICT') return 'The workspace changed while this was offline. Use the latest version or retry after review.'
  if (kind === 'HIDDEN') return 'This task is no longer available for field work.'
  if (kind === 'MISSING_LOCAL_FILE') return 'The local file is no longer on this device. Attach the proof again.'
  if (kind === 'UPLOAD') return 'The upload did not finish. Check connection or quota, then retry.'
  return error || 'This operation needs attention before it can sync.'
}

export const getMobileOperationState = (
  record: Pick<HelperMutationRecord, 'status'>,
  phase?: HelperOperationPhase,
): MobileOperationState => {
  if (record.status === 'FAILED' || record.status === 'ACCESS_LOST' || record.status === 'EXPIRED') return 'FAILED'
  if (record.status === 'COMPLETED') return 'SYNCED'
  if (phase === 'UPLOADING') return 'UPLOADING'
  if (phase === 'FINALIZING') return 'FINALIZING'
  if (phase === 'SYNCING' || record.status === 'SYNCING') return 'SYNCING'
  return 'QUEUED'
}

export const getMobileOperationStateLabel = (state: MobileOperationState): string => {
  if (state === 'QUEUED') return 'Queued'
  if (state === 'UPLOADING') return 'Uploading'
  if (state === 'FINALIZING') return 'Finalizing'
  if (state === 'SYNCING') return 'Syncing'
  if (state === 'FAILED') return 'Needs action'
  return 'Synced'
}

export const buildMobileSyncSummary = (input: MobileSyncSnapshotInput): {
  tone: MobileSyncTone
  headline: string
  detail: string
} => {
  if (!input.isOnline || input.syncState === 'OFFLINE') {
    const queued = input.pendingCount + input.failedCount
    return {
      tone: 'offline',
      headline: 'Offline mode',
      detail: queued > 0
        ? `${queued} ${queued === 1 ? 'update is' : 'updates are'} held safely on this device.`
        : 'Safe actions will queue until connection returns.',
    }
  }
  if (input.failedCount > 0 || input.syncState === 'ERROR') {
    return {
      tone: 'attention',
      headline: 'Sync needs action',
      detail: `${input.failedCount || 1} ${input.failedCount === 1 ? 'item needs' : 'items need'} review before syncing.`,
    }
  }
  if (input.syncState === 'SYNCING' || input.pendingCount > 0) {
    return {
      tone: 'working',
      headline: 'Syncing work',
      detail: `${input.pendingCount} ${input.pendingCount === 1 ? 'update is' : 'updates are'} being sent.`,
    }
  }
  if (input.completedCount > 0) {
    return {
      tone: 'healthy',
      headline: 'Work synced',
      detail: 'Recent field updates reached the workspace.',
    }
  }
  return {
    tone: 'healthy',
    headline: 'Ready to work',
    detail: input.lastSyncAt ? `Last synced ${new Date(input.lastSyncAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.` : 'No pending mobile updates.',
  }
}
